import { coordEq, unitAt } from "@dailywars/core/engine";
import type { Coord, GameState, Unit } from "@dailywars/core/engine";

import { canShowUnloadCursor } from "#/game/selection/intents/tileSelectSharedHelpers";
import { getSelectUnitHoverTarget } from "#/game/selection/selectUnitHover";
import { canCaptureAt, canOpenBuildMenu } from "#/game/selection/transitions";
import type {
  AttackOption,
  BoardCursorMode,
  JoinOption,
  LoadOption,
  SelectionState,
} from "#/game/selection/types";

function findSelectedUnit(state: GameState, unitId: string): Unit | undefined {
  return state.units.find((u) => u.id === unitId);
}

function isAttackableEnemy({
  attackOptions,
  hoveredUnit,
  hoveredEnemy,
}: {
  attackOptions: AttackOption[];
  hoveredUnit: Unit | undefined;
  hoveredEnemy: boolean;
}): boolean {
  return Boolean(
    hoveredEnemy && hoveredUnit && attackOptions.some((o) => o.targetId === hoveredUnit.id),
  );
}

function isFriendlyUnit(hoveredUnit: Unit | undefined, currentPlayer: string): boolean {
  return Boolean(hoveredUnit && hoveredUnit.owner === currentPlayer);
}

function hasOptionForUnit<T extends { targetId: string }>(
  options: T[],
  hoveredUnit: Unit | undefined,
): boolean {
  return Boolean(hoveredUnit && options.some((o) => o.targetId === hoveredUnit.id));
}

function isReachableTile(reachable: Coord[], hoveredTile: Coord): boolean {
  return reachable.some((c) => coordEq(c, hoveredTile));
}

function cursorAtUnitPosition(state: GameState, unit: Unit): BoardCursorMode {
  if (canShowUnloadCursor(unit)) return "download";
  return canCaptureAt({ state, unit, pos: unit.pos }) ? "capture" : "wait";
}

function cursorForFriendlyLoadOrJoin({
  hoveredUnit,
  currentPlayer,
  loadOptions,
  joinOptions,
  loadCursor,
  joinCursor,
}: {
  hoveredUnit: Unit | undefined;
  currentPlayer: string;
  loadOptions: LoadOption[];
  joinOptions: JoinOption[];
  loadCursor: "move" | "load";
  joinCursor: "move" | "join";
}): BoardCursorMode | null {
  if (!isFriendlyUnit(hoveredUnit, currentPlayer)) return null;
  if (hasOptionForUnit(loadOptions, hoveredUnit)) return loadCursor;
  if (hasOptionForUnit(joinOptions, hoveredUnit)) return joinCursor;
  return null;
}

function cursorForTransportUnload({
  selection,
  unit,
  hoveredTile,
}: {
  selection: Extract<SelectionState, { mode: "transport-unload" }>;
  unit: Unit;
  hoveredTile: Coord | null;
}): BoardCursorMode {
  if (!hoveredTile) return "wait";
  if (coordEq(hoveredTile, unit.pos)) return "wait";
  if (selection.unloadTiles.some((c) => coordEq(c, hoveredTile))) return "download";
  return "default";
}

function cursorOffActionTile({
  state,
  selection,
  hoveredTile,
  hoveredUnit,
  hoveredEnemy,
}: {
  state: GameState;
  selection: Extract<SelectionState, { mode: "unit" | "move-preview" }>;
  hoveredTile: Coord;
  hoveredUnit: Unit | undefined;
  hoveredEnemy: boolean;
}): BoardCursorMode {
  if (isAttackableEnemy({ attackOptions: selection.attackOptions, hoveredUnit, hoveredEnemy })) {
    return "attack";
  }
  if (isReachableTile(selection.reachable, hoveredTile)) {
    return "move";
  }
  const friendlyCursor = cursorForFriendlyLoadOrJoin({
    hoveredUnit,
    currentPlayer: state.currentPlayer,
    loadOptions: selection.loadOptions,
    joinOptions: selection.joinOptions,
    loadCursor: "move",
    joinCursor: "move",
  });
  return friendlyCursor ?? "default";
}

function cursorForUnitMode({
  state,
  selection,
  unit,
  hoveredTile,
  hoveredUnit,
  hoveredEnemy,
}: {
  state: GameState;
  selection: Extract<SelectionState, { mode: "unit" }>;
  unit: Unit;
  hoveredTile: Coord;
  hoveredUnit: Unit | undefined;
  hoveredEnemy: boolean;
}): BoardCursorMode {
  if (coordEq(hoveredTile, unit.pos)) {
    return cursorAtUnitPosition(state, unit);
  }
  return cursorOffActionTile({ state, selection, hoveredTile, hoveredUnit, hoveredEnemy });
}

function cursorForMovePreview({
  state,
  selection,
  unit,
  hoveredTile,
  hoveredUnit,
  hoveredEnemy,
}: {
  state: GameState;
  selection: Extract<SelectionState, { mode: "move-preview" }>;
  unit: Unit;
  hoveredTile: Coord;
  hoveredUnit: Unit | undefined;
  hoveredEnemy: boolean;
}): BoardCursorMode {
  if (isAttackableEnemy({ attackOptions: selection.attackOptions, hoveredUnit, hoveredEnemy })) {
    return "attack";
  }
  if (!coordEq(hoveredTile, selection.destination)) {
    return cursorOffActionTile({ state, selection, hoveredTile, hoveredUnit, hoveredEnemy });
  }
  const friendlyCursor = cursorForFriendlyLoadOrJoin({
    hoveredUnit,
    currentPlayer: state.currentPlayer,
    loadOptions: selection.loadOptions,
    joinOptions: selection.joinOptions,
    loadCursor: "load",
    joinCursor: "join",
  });
  if (friendlyCursor) return friendlyCursor;
  if (canShowUnloadCursor(unit)) return "download";
  return canCaptureAt({ state, unit, pos: selection.destination }) ? "capture" : "wait";
}

function activeUnitCursorTile({
  selection,
  unit,
  hoveredTile,
}: {
  selection: Extract<SelectionState, { mode: "unit" | "move-preview" }>;
  unit: Unit;
  hoveredTile: Coord | null;
}): Coord {
  if (hoveredTile) return hoveredTile;
  return selection.mode === "move-preview" ? selection.destination : unit.pos;
}

function cursorForActiveUnit({
  state,
  selection,
  hoveredTile,
}: {
  state: GameState;
  selection: Extract<SelectionState, { mode: "unit" | "move-preview" }>;
  hoveredTile: Coord | null;
}): BoardCursorMode {
  const unit = findSelectedUnit(state, selection.unitId);
  if (!unit) return "default";

  const cursorTile = activeUnitCursorTile({ selection, unit, hoveredTile });
  const hoveredUnit = unitAt(state, cursorTile);
  const hoveredEnemy = Boolean(hoveredUnit && hoveredUnit.owner !== state.currentPlayer);

  if (selection.mode === "unit") {
    return cursorForUnitMode({
      state,
      selection,
      unit,
      hoveredTile: cursorTile,
      hoveredUnit,
      hoveredEnemy,
    });
  }
  return cursorForMovePreview({
    state,
    selection,
    unit,
    hoveredTile: cursorTile,
    hoveredUnit,
    hoveredEnemy,
  });
}

export function getBoardCursorMode({
  state,
  selection,
  hoveredTile,
  coarsePointer = false,
}: {
  state: GameState;
  selection: SelectionState;
  hoveredTile: Coord | null;
  coarsePointer?: boolean;
}): BoardCursorMode {
  if (hoveredTile && canOpenBuildMenu(state, hoveredTile)) return "build";

  switch (selection.mode) {
    case "none":
    case "building":
    case "enemy-inspect":
      if (getSelectUnitHoverTarget({ state, selection, hoveredTile, coarsePointer })) {
        return "select";
      }
      return "default";
    case "confirm-attack":
      return "attack";
    case "transport-unload": {
      const unit = findSelectedUnit(state, selection.unitId);
      if (!unit) return "default";
      return cursorForTransportUnload({ selection, unit, hoveredTile });
    }
    case "unit":
    case "move-preview": {
      const mode = cursorForActiveUnit({ state, selection, hoveredTile });
      if (
        mode === "default" &&
        getSelectUnitHoverTarget({ state, selection, hoveredTile, coarsePointer })
      ) {
        return "select";
      }
      return mode;
    }
  }
}
